/**
 * @file useFetchData.js
 * @author Rishikesh
 * @date 2024-09-09
 * @description Hook used to manage the api callings simple by retuning data , error and the loading state for the api callings.
 */
import { useState, useCallback } from "react";
import axios from "axios";
import { useSelector } from "react-redux";

export const useFetchData = (baseUrl = process.env.REACT_APP_API_URL) => {
  const [error, setError] = useState({});
  const [loading, setLoading] = useState({});
  const ssid = useSelector((state) => state.auth.ssid);

  const fetchData = useCallback(
    async (apiname, { method = "get", body = null } = {}) => {
      const apiUrl = `${baseUrl}${apiname}`;
      const defaultHeaders = {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(ssid && { ssid }),
      };

      setLoading((prev) => ({ ...prev, [apiname]: true }));
      setError((prev) => ({ ...prev, [apiname]: null }));

      try {
        // Handle GET and POST requests
        const response =
          method === "get"
            ? await axios.get(apiUrl, { headers: defaultHeaders })
            : await axios.post(apiUrl, body, { headers: defaultHeaders });

        return response.data; // Return data directly
      } catch (err) {
        setError((prev) => ({
          ...prev,
          [apiname]: err.response?.data || err.message,
        }));
        return null; // Return null on error
      } finally {
        setLoading((prev) => ({ ...prev, [apiname]: false }));
      }
    },
    [baseUrl, ssid]
  );

  return { fetchData, error, loading };
};
